import { useMemo, useState } from 'react';
import { TextStyle, View } from 'react-native';
import StyleSheet from 'react-native-media-query';
import { ButtonProps, Text, useTheme } from '@ui-kitten/components';
import { EvaStatus } from '@ui-kitten/components/devsupport';
import lodashIsEmpty from 'lodash/isEmpty';
import lodashIsUndefined from 'lodash/isUndefined';

import ThemeStyle, { globalSpacing } from '../../../../Theme/styles';
import BreakPoints from '../../../../Theme/styles/breakpoints';

import Button from '../../../Button';
import HighlightText from '../../../HighlightText';

import { MODALPROMPT } from './config';
import useModalPrompt from './hooks/useModalPrompt';
import ModalPrompt from './ModalPrompt';

interface ButtonsProps extends ButtonProps {
  status?: EvaStatus;
  text: string;
  value: any;
  onPress?: (data?: any) => Promise<void> | void;
}

type PromptState = {
  title: string;
  message: string;
  highlight?: [{ word: string; style: TextStyle }];
  buttonStatus?: EvaStatus;
  buttonText?: string;
  buttons?: Array<ButtonsProps>;
  withButtonLoader?: boolean;
  onCloseButtonClick?: () => void;
};

const Prompt = () => {
  const { hideModalPrompt } = useModalPrompt();
  const theme = useTheme();
  const [data, setData] = useState<PromptState | undefined>();
  const [loadingIndex, setLoadingIndex] = useState<number | undefined>();

  const title = data?.title || '';
  const message = data?.message || '';
  const highlight = data?.highlight || [];
  const buttonStatus = data?.buttonStatus || '';
  const buttonText = data?.buttonText || '';
  const buttons = data?.buttons || [];
  const withButtonLoader = data?.withButtonLoader || false;
  const onCloseButtonClick = data?.onCloseButtonClick;

  const hasButton =
    (Boolean(buttonStatus) && Boolean(buttonText)) || !lodashIsEmpty(buttons);

  const closeIconColor = useMemo(() => {
    if (theme?.icon_color) {
      return theme?.icon_color;
    } else if (buttonStatus) {
      return theme[`color-${buttonStatus}-500`];
    }
    return undefined;
  }, [buttonStatus, theme]);

  const _onClose = (data?: ButtonsProps | { buttonClicked: boolean }) =>
    hideModalPrompt(MODALPROMPT.prompt, data);

  const _onCloseButtonClick = () => {
    _onClose({ text: 'x', value: 'x' });
    onCloseButtonClick();
  };

  const _onClick = async (button: ButtonsProps, index: number) => {
    if (button.onPress) {
      if (withButtonLoader) {
        setLoadingIndex(index);
        await button.onPress?.(button);
        setLoadingIndex(undefined);
      } else {
        button.onPress?.(button);
      }
    }
    _onClose();
  };

  const _renderButton = () => {
    if (!lodashIsEmpty(buttons)) {
      const isRow = buttons.length <= 2;
      return (
        <View
          dataSet={{ media: ids.buttonContainer }}
          //@ts-ignore : rowgap and columngap definition not avaialble in react native media query
          style={{
            ...(isRow && styles.buttonContainer),
            ...{ rowGap: globalSpacing / 2, columnGap: globalSpacing / 2 },
          }}
        >
          {buttons.map((button, i) => {
            const { text, onPress, ...rest } = button;
            const hasLoading = !lodashIsUndefined(loadingIndex);
            return (
              <Button
                key={i}
                loading={i === loadingIndex}
                disabled={hasLoading ? i !== loadingIndex : false}
                onPress={() => _onClick(button, i)}
                {...rest}
              >
                {(props: any) => (
                  <Text {...props} numberOfLines={isRow ? 1 : undefined}>
                    {text}
                  </Text>
                )}
              </Button>
            );
          })}
        </View>
      );
    } else if (Boolean(buttonStatus) && Boolean(buttonText)) {
      return (
        <Button
          status={buttonStatus}
          onPress={() =>
            _onClose({
              buttonClicked: true,
              text: buttonText,
              value: buttonText,
            })
          }
        >
          {() => <Text appearance="alternative">{buttonText}</Text>}
        </Button>
      );
    }
    return null;
  };

  return (
    <ModalPrompt
      id={MODALPROMPT.prompt}
      dataSet={{ media: ids.promptModal }}
      mainContentWrapperStyle={styles.promptModal}
      closeButtonColor={closeIconColor}
      closeOnDocumentClick={false}
      title={title}
      onBeforeShow={setData}
      onCloseButtonClick={onCloseButtonClick ? _onCloseButtonClick : undefined}
      hasHeader
      hasHeaderLine
    >
      <View style={hasButton && ThemeStyle.spacingBottomMedium}>
        <HighlightText highlight={highlight} text={message} />
      </View>
      {_renderButton()}
    </ModalPrompt>
  );
};

const { ids, styles } = StyleSheet.create({
  promptModal: {
    [`@media ${BreakPoints.xs}`]: {
      width: 300,
    },
    [`@media ${BreakPoints.sm}`]: {
      width: 320,
    },
    [`@media ${BreakPoints.md}`]: {
      width: 340,
    },
    [`@media ${BreakPoints.lg}`]: {
      width: 360,
    },
    [`@media ${BreakPoints.xl}`]: {
      width: 380,
    },
    [`@media ${BreakPoints.xxl}`]: {
      width: 400,
    },
  },
  buttonContainer: {
    [`@media ${BreakPoints.xs}`]: {
      ...ThemeStyle.flexDirectionRowCenterCenter,
    },
    [`@media ${BreakPoints.sm}`]: {
      ...ThemeStyle.flexDirectionRowCenterCenter,
    },
    [`@media ${BreakPoints.md}`]: {
      ...ThemeStyle.flexDirectionRowEndEnd,
    },
    [`@media ${BreakPoints.lg}`]: {
      ...ThemeStyle.flexDirectionRowEndEnd,
    },
    [`@media ${BreakPoints.xl}`]: {
      ...ThemeStyle.flexDirectionRowEndEnd,
    },
    [`@media ${BreakPoints.xxl}`]: {
      ...ThemeStyle.flexDirectionRowEndEnd,
    },
  },
});

export default Prompt;
